import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { getAuth } from 'firebase/auth';
import { db } from '../../../../firebase-client';
import { normalizeSailingCrewFinderSettings } from '../../../teams/utils/moduleUtils';
import {
  canPostToCrewFinder,
  crewFinderPostTypeLabel,
  crewFinderStatusLabel,
  safeCrewFinderBoatLabel,
} from '../../../teams/utils/sailingCrewFinder';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function dateFromISO(value) {
  const iso = String(value || '').trim();
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatPostDate(post = {}) {
  const date = post?.date?.toDate?.() || dateFromISO(post?.dateIso);
  const dateLabel = date ? date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date TBC';
  const times = [post.fromTime, post.toTime].filter(Boolean).join('-');
  return [dateLabel, times].filter(Boolean).join(' · ');
}

function cleanText(value, max = 240) {
  return String(value || '').trim().slice(0, max);
}

function isValidTimeInput(value) {
  const raw = String(value || '').trim();
  if (!raw) return true;
  return /^\d{2}:\d{2}$/.test(raw);
}

function memberDisplayName(profile = {}, fallback = {}) {
  return cleanText(profile?.name || profile?.displayName || fallback?.displayName || fallback?.email || profile?.email || 'Member', 120);
}

function sortByUpdated(rows = []) {
  return [...rows].sort((left, right) => {
    const leftValue = left?.updatedAt?.toMillis?.() || left?.createdAt?.toMillis?.() || 0;
    const rightValue = right?.updatedAt?.toMillis?.() || right?.createdAt?.toMillis?.() || 0;
    return rightValue - leftValue;
  });
}

function crewFinderResponseStatusLabel(status) {
  const normalized = String(status || 'interested').trim();
  if (normalized === 'accepted') return 'Accepted';
  if (normalized === 'declined') return 'Declined';
  if (normalized === 'withdrawn') return 'Withdrawn';
  return 'Interested';
}

function interestActionLabel(post = {}) {
  return post?.type === 'available_to_crew' ? 'Invite / contact' : 'I can crew';
}

function responseHelperText(post = {}) {
  return post?.type === 'available_to_crew'
    ? 'This sends your name and message to the member offering availability.'
    : 'This sends your name and message to the organiser looking for crew.';
}

function primaryPostActionLabel(post = {}) {
  return post?.type === 'available_to_crew' ? 'Close availability' : 'Mark crew found';
}

function summarizePostForLog(payload = {}) {
  return {
    keys: Object.keys(payload).sort(),
    type: payload.type,
    hasTitle: Boolean(payload.title),
    dateIso: payload.dateIso || '',
    fromTime: payload.fromTime || '',
    toTime: payload.toTime || '',
    eventLinked: Boolean(payload.eventId),
    boatSourceType: payload.boatSourceType || '',
    hasBoatId: Boolean(payload.boatId),
    hasBoatBookingRequestId: Boolean(payload.boatBookingRequestId),
    hasMooringAllocationId: Boolean(payload.mooringAllocationId),
    hasBoatLabelSnapshot: Boolean(payload.boatLabelSnapshot),
    hasNotes: Boolean(payload.notes),
    hasPreferredRoles: Boolean(payload.preferredRoles),
  };
}

const ACTIVE_BOOKING_REQUEST_STATUSES = new Set(['pending', 'allocated', 'confirmed', 'reserved', 'active', '']);

function formatBookingRequestDate(request = {}) {
  const date = request?.date?.toDate?.() || dateFromISO(request?.dateIso);
  const dateLabel = date ? date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date TBC';
  const times = [request.fromTime, request.toTime].filter(Boolean).join('-');
  return [dateLabel, times].filter(Boolean).join(' · ');
}

function formatBookingRequestLabel(request = {}, boatsById = new Map()) {
  const selectedBoat = request.selectedBoatId ? boatsById.get(request.selectedBoatId) : null;
  const selectedLabel = cleanText(request.selectedBoatName, 120)
    || (selectedBoat ? safeCrewFinderBoatLabel({ boatSourceType: 'club_boat', clubBoat: selectedBoat }) : '');
  const prefLabels = Array.isArray(request.preferences)
    ? request.preferences
      .map((boatId) => {
        const boat = boatsById.get(boatId);
        return boat ? safeCrewFinderBoatLabel({ boatSourceType: 'club_boat', clubBoat: boat }) : '';
      })
      .filter(Boolean)
      .slice(0, 2)
    : [];
  const boatLabel = selectedLabel || (prefLabels.length ? `Requested: ${prefLabels.join(', ')}` : 'Boat booking request');
  return [boatLabel, formatBookingRequestDate(request), String(request.status || 'pending')].filter(Boolean).join(' · ');
}

function createEmptyDraft(type = 'crew_needed') {
  return {
    type,
    title: '',
    dateIso: todayISO(),
    fromTime: '',
    toTime: '',
    eventId: '',
    boatSourceType: '',
    clubBoatId: '',
    boatBookingRequestId: '',
    mooringAllocationId: '',
    boatLabel: '',
    notes: '',
    preferredRoles: '',
  };
}

export default function EditSailingCrewFinderForm({
  isOpen,
  teamId,
  team = {},
  user = null,
  memberProfile = null,
  memberTags = [],
  memberProfiles = [],
  teamTags = [],
  isAdmin = false,
  initialTab = 'crew_needed',
  onClose,
}) {
  const uid = getAuth()?.currentUser?.uid || user?.uid || '';
  const [activeTab, setActiveTab] = useState(initialTab || 'crew_needed');
  const [posts, setPosts] = useState([]);
  const [openPostsForMembers, setOpenPostsForMembers] = useState([]);
  const [ownPostsForMembers, setOwnPostsForMembers] = useState([]);
  const [responsesByPost, setResponsesByPost] = useState({});
  const [boats, setBoats] = useState([]);
  const [boatRequests, setBoatRequests] = useState([]);
  const [events, setEvents] = useState([]);
  const [moorings, setMoorings] = useState([]);
  const [draft, setDraft] = useState(createEmptyDraft('crew_needed'));
  const [responseDrafts, setResponseDrafts] = useState({});
  const [settingsDraft, setSettingsDraft] = useState(normalizeSailingCrewFinderSettings(team?.modules?.sailing?.crewFinder || {}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touchedFields, setTouchedFields] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const modules = useMemo(() => team?.modules || {}, [team?.modules]);
  const settings = useMemo(() => normalizeSailingCrewFinderSettings(modules?.sailing?.crewFinder || {}), [modules]);
  const profileById = useMemo(() => new Map(memberProfiles.map((profile) => [profile.id, profile])), [memberProfiles]);
  const boatsById = useMemo(() => new Map(boats.map((boat) => [boat.id, boat])), [boats]);
  const editableTagNames = useMemo(() => Array.from(new Set(
    (Array.isArray(teamTags) ? teamTags : [])
      .filter((tag) => tag?.type !== 'ageRule')
      .map((tag) => cleanText(tag?.name, 80))
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [teamTags]);

  const canPostCrewNeeded = canPostToCrewFinder({ modules, memberTags, isAdmin, type: 'crew_needed' });
  const canPostAvailability = canPostToCrewFinder({ modules, memberTags, isAdmin, type: 'available_to_crew' });
  const canActAsMember = Boolean(uid && memberProfile);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(initialTab || 'crew_needed');
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSettingsDraft(settings);
    setDraft(createEmptyDraft(activeTab === 'available_to_crew' ? 'available_to_crew' : 'crew_needed'));
    setError('');
    setSuccess('');
    setTouchedFields({});
    setSubmitAttempted(false);
  }, [activeTab, isOpen, settings]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    const postsRef = collection(db, 'teams', teamId, 'sailingCrewFinderPosts');
    if (isAdmin) {
      const unsubscribe = safeOnSnapshot(postsRef, (snapshot) => {
        setPosts(sortByUpdated(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))));
      }, undefined, { label: 'sailing-crew-finder-admin-posts', teamId });
      return () => unsubscribe();
    }
    if (!uid) {
      setPosts([]);
      setOpenPostsForMembers([]);
      setOwnPostsForMembers([]);
      return undefined;
    }
    const unsubscribeOpen = safeOnSnapshot(query(postsRef, where('status', '==', 'open')), (snapshot) => {
      setOpenPostsForMembers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-crew-finder-open-posts', teamId });
    const unsubscribeOwn = safeOnSnapshot(query(postsRef, where('createdBy', '==', uid)), (snapshot) => {
      setOwnPostsForMembers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-crew-finder-own-posts', teamId });
    return () => {
      unsubscribeOpen();
      unsubscribeOwn();
    };
  }, [isAdmin, isOpen, teamId, uid]);

  useEffect(() => {
    if (isAdmin) return;
    const byId = new Map();
    openPostsForMembers.forEach((post) => byId.set(post.id, post));
    ownPostsForMembers.forEach((post) => byId.set(post.id, post));
    setPosts(sortByUpdated(Array.from(byId.values())));
  }, [isAdmin, openPostsForMembers, ownPostsForMembers]);

  useEffect(() => {
    if (!isOpen || !teamId || posts.length === 0) {
      setResponsesByPost({});
      return undefined;
    }
    const unsubscribes = posts.slice(0, 30).map((post) => {
      const responsesRef = collection(db, 'teams', teamId, 'sailingCrewFinderPosts', post.id, 'responses');
      const responsesQuery = isAdmin || post.createdBy === uid
        ? responsesRef
        : query(responsesRef, where('memberId', '==', uid));
      return safeOnSnapshot(
        responsesQuery,
      (snapshot) => {
        setResponsesByPost((current) => ({
          ...current,
          [post.id]: snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
        }));
      },
      undefined,
      { label: 'sailing-crew-finder-responses', teamId, postId: post.id }
      );
    });
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [isAdmin, isOpen, posts, teamId, uid]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const boatsSnap = await getDocs(collection(db, 'teams', teamId, 'sailingBoats'));
        if (!cancelled) setBoats(boatsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch {
        if (!cancelled) setBoats([]);
      }
      try {
        const eventsSnap = await getDocs(collection(db, 'teams', teamId, 'events'));
        if (!cancelled) setEvents(eventsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId || !uid) return undefined;
    let cancelled = false;
    const allocationQuery = isAdmin
      ? collection(db, 'teams', teamId, 'mooringAllocations')
      : query(collection(db, 'teams', teamId, 'mooringAllocations'), where('memberId', '==', uid));
    getDocs(allocationQuery)
      .then((snapshot) => {
        if (!cancelled) setMoorings(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      })
      .catch(() => {
        if (!cancelled) setMoorings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isOpen, teamId, uid]);

  useEffect(() => {
    if (!isOpen || !teamId || !uid) return undefined;
    let cancelled = false;
    const requestQuery = isAdmin
      ? collection(db, 'teams', teamId, 'sailingBoatRequests')
      : query(collection(db, 'teams', teamId, 'sailingBoatRequests'), where('memberId', '==', uid));
    getDocs(requestQuery)
      .then((snapshot) => {
        if (cancelled) return;
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((request) => ACTIVE_BOOKING_REQUEST_STATUSES.has(String(request.status || '')));
        setBoatRequests(rows.sort((left, right) => {
          const leftValue = left?.date?.toMillis?.() || left?.createdAt?.toMillis?.() || 0;
          const rightValue = right?.date?.toMillis?.() || right?.createdAt?.toMillis?.() || 0;
          return leftValue - rightValue;
        }));
      })
      .catch(() => {
        if (!cancelled) setBoatRequests([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isOpen, teamId, uid]);

  if (!isOpen) return null;

  const openPosts = posts.filter((post) => String(post.status || 'open') === 'open');
  const crewNeededPosts = openPosts.filter((post) => post.type === 'crew_needed');
  const availabilityPosts = openPosts.filter((post) => post.type === 'available_to_crew');
  const myPosts = posts.filter((post) => post.createdBy === uid);
  const visiblePosts = activeTab === 'available_to_crew'
    ? availabilityPosts
    : activeTab === 'my_posts'
      ? myPosts
      : activeTab === 'admin'
        ? posts
        : crewNeededPosts;

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setSuccess('');
  };
  const markFieldTouched = (field) => setTouchedFields((current) => ({ ...current, [field]: true }));
  const updateBoatSourceType = (value) => setDraft((current) => ({
    ...current,
    boatSourceType: value,
    clubBoatId: value === 'club_boat' ? current.clubBoatId : '',
    boatBookingRequestId: value === 'club_boat_booking' ? current.boatBookingRequestId : '',
    mooringAllocationId: value === 'mooring_allocation' ? current.mooringAllocationId : '',
  }));
  const selectedEvent = events.find((event) => event.id === draft.eventId) || null;
  const selectedBoat = boatsById.get(draft.clubBoatId) || null;
  const selectedBoatRequest = boatRequests.find((request) => request.id === draft.boatBookingRequestId) || null;
  const selectedMooring = moorings.find((allocation) => allocation.id === draft.mooringAllocationId) || null;
  const boatSourceOptions = [
    ...(boats.length > 0 ? [['club_boat', 'Club boat']] : []),
    ...(boatRequests.length > 0 ? [['club_boat_booking', 'Boat booking']] : []),
    ...(moorings.length > 0 ? [['mooring_allocation', 'Mooring vessel']] : []),
    ['own_boat', 'Own/other boat'],
  ];
  const effectiveBoatSourceType = draft.boatSourceType && boatSourceOptions.some(([value]) => value === draft.boatSourceType)
    ? draft.boatSourceType
    : boatSourceOptions[0]?.[0] || 'own_boat';
  const fieldErrors = {
    title: cleanText(draft.title, 120) ? '' : 'Add a title.',
    dateIso: dateFromISO(draft.dateIso) ? '' : 'Choose a valid date.',
    fromTime: isValidTimeInput(draft.fromTime) ? '' : 'Use 24-hour time, e.g. 10:30.',
    toTime: isValidTimeInput(draft.toTime) ? '' : 'Use 24-hour time, e.g. 14:00.',
  };
  const visibleFieldError = (field) => (submitAttempted || touchedFields[field] ? fieldErrors[field] : '');

  async function savePost(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSubmitAttempted(true);
    const type = activeTab === 'available_to_crew' ? 'available_to_crew' : 'crew_needed';
    const allowed = type === 'available_to_crew' ? canPostAvailability : canPostCrewNeeded;
    if (!allowed) {
      setError('You do not have access to post in Crew Finder.');
      return;
    }
    const date = dateFromISO(draft.dateIso);
    const title = cleanText(draft.title, 120);
    if (!title || !date || !isValidTimeInput(draft.fromTime) || !isValidTimeInput(draft.toTime)) {
      setTouchedFields((current) => ({
        ...current,
        title: true,
        dateIso: true,
        fromTime: true,
        toTime: true,
      }));
      return;
    }
    const boatSourceType = type === 'crew_needed' ? effectiveBoatSourceType : '';
    if (type === 'crew_needed' && boatSourceType === 'club_boat' && !selectedBoat) {
      setError('Choose a club boat, or use Own/other boat.');
      return;
    }
    if (type === 'crew_needed' && boatSourceType === 'club_boat_booking' && !selectedBoatRequest) {
      setError('Choose a boat booking request, or use Own/other boat.');
      return;
    }
    if (type === 'crew_needed' && boatSourceType === 'mooring_allocation' && !selectedMooring) {
      setError('Choose a mooring vessel, or use Own/other boat.');
      return;
    }
    const boatLabelSnapshot = type === 'crew_needed'
      ? safeCrewFinderBoatLabel({
        boatSourceType,
        clubBoat: selectedBoat,
        mooringAllocation: selectedMooring,
        fallback: boatSourceType === 'club_boat_booking'
          ? formatBookingRequestLabel(selectedBoatRequest, boatsById)
          : cleanText(draft.boatLabel, 120) || 'Boat TBC',
      })
      : '';
    if (type === 'crew_needed' && !boatLabelSnapshot) {
      setError('Add a boat or arrangement label.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type,
        status: 'open',
        title,
        date: Timestamp.fromDate(date),
        dateIso: draft.dateIso,
        fromTime: cleanText(draft.fromTime, 12),
        toTime: cleanText(draft.toTime, 12),
        eventId: selectedEvent?.id || '',
        eventNameSnapshot: cleanText(selectedEvent?.name || selectedEvent?.title || '', 120),
        boatSourceType,
        boatLabelSnapshot,
        notes: cleanText(draft.notes, 800),
        preferredRoles: cleanText(draft.preferredRoles, 240),
        createdBy: uid,
        createdByNameSnapshot: memberDisplayName(memberProfile, user),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (boatSourceType === 'club_boat') {
        payload.boatId = selectedBoat?.id || '';
      } else if (boatSourceType === 'club_boat_booking') {
        payload.boatBookingRequestId = selectedBoatRequest?.id || '';
      } else if (boatSourceType === 'mooring_allocation') {
        payload.mooringAllocationId = selectedMooring?.id || '';
      }
      const docRef = await addDoc(collection(db, 'teams', teamId, 'sailingCrewFinderPosts'), payload);
      const localNow = Timestamp.now();
      const localPost = {
        ...payload,
        id: docRef.id,
        createdAt: localNow,
        updatedAt: localNow,
      };
      setPosts((current) => sortByUpdated([localPost, ...current.filter((post) => post.id !== docRef.id)]));
      setOpenPostsForMembers((current) => sortByUpdated([localPost, ...current.filter((post) => post.id !== docRef.id)]));
      setOwnPostsForMembers((current) => sortByUpdated([localPost, ...current.filter((post) => post.id !== docRef.id)]));
      setDraft(createEmptyDraft(type));
      setTouchedFields({});
      setSubmitAttempted(false);
      setSuccess('Post saved.');
    } catch (err) {
      const message = String(err?.message || '');
      if (typeof console !== 'undefined') {
        console.warn('[Crew Finder] direct create failed', {
          code: err?.code || '',
          message,
          isAdmin,
          type,
          allowed,
          activeTab,
          boatSourceType,
          sourceDiagnostics: {
            boatCount: boats.length,
            boatRequestCount: boatRequests.length,
            mooringCount: moorings.length,
            selectedClubBoat: Boolean(selectedBoat),
            selectedBoatBookingRequest: Boolean(selectedBoatRequest),
            selectedMooring: Boolean(selectedMooring),
            sourceOptions: boatSourceOptions.map(([value]) => value),
          },
          post: summarizePostForLog({
            type,
            title,
            dateIso: draft.dateIso,
            fromTime: draft.fromTime,
            toTime: draft.toTime,
            eventId: selectedEvent?.id || '',
            boatSourceType,
            boatLabelSnapshot,
            notes: draft.notes,
            preferredRoles: draft.preferredRoles,
            boatId: boatSourceType === 'club_boat' ? selectedBoat?.id || '' : '',
            boatBookingRequestId: boatSourceType === 'club_boat_booking' ? selectedBoatRequest?.id || '' : '',
            mooringAllocationId: boatSourceType === 'mooring_allocation' ? selectedMooring?.id || '' : '',
          }),
        });
      }
      setError(
        message.toLowerCase().includes('permission')
          ? 'Crew Finder posting is blocked by the saved posting rules or this member cannot access the Crew Finder card.'
          : message || 'Could not save this post.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function updatePostStatus(post, status) {
    if (!post?.id) return;
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId, 'sailingCrewFinderPosts', post.id), {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err?.message || 'Could not update this post.');
    }
  }

  async function respondToPost(post) {
    if (!post?.id || !uid) return;
    if (!canActAsMember) {
      setError('You need a member profile on this team to register interest.');
      return;
    }
    const message = cleanText(responseDrafts[post.id], 500);
    setError('');
    try {
      const payload = {
        memberId: uid,
        memberNameSnapshot: memberDisplayName(memberProfile, user),
        message,
        status: 'interested',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, 'teams', teamId, 'sailingCrewFinderPosts', post.id, 'responses'), payload);
      const localNow = Timestamp.now();
      setResponsesByPost((current) => ({
        ...current,
        [post.id]: [
          { ...payload, id: docRef.id, createdAt: localNow, updatedAt: localNow },
          ...(current[post.id] || []).filter((response) => response.id !== docRef.id),
        ],
      }));
      setResponseDrafts((current) => ({ ...current, [post.id]: '' }));
      setSuccess('Interest sent.');
    } catch (err) {
      setError(err?.message || 'Could not send your response.');
    }
  }

  async function updateResponseStatus(post, response, status) {
    if (!post?.id || !response?.id) return;
    setError('');
    setSuccess('');
    try {
      await updateDoc(doc(db, 'teams', teamId, 'sailingCrewFinderPosts', post.id, 'responses', response.id), {
        status,
        updatedAt: serverTimestamp(),
      });
      setResponsesByPost((current) => ({
        ...current,
        [post.id]: (current[post.id] || []).map((row) => (
          row.id === response.id ? { ...row, status, updatedAt: Timestamp.now() } : row
        )),
      }));
      setSuccess(status === 'withdrawn' ? 'Interest withdrawn.' : `Response ${crewFinderResponseStatusLabel(status).toLowerCase()}.`);
    } catch (err) {
      setError(err?.message || 'Could not update this response.');
    }
  }

  async function saveSettings(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    const next = normalizeSailingCrewFinderSettings({ ...settingsDraft, enabled: true });
    setSaving(true);
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.sailing.crewFinder': next,
      });
      setSuccess('Crew Finder settings saved.');
    } catch (err) {
      setError(err?.message || 'Could not save Crew Finder settings.');
    } finally {
      setSaving(false);
    }
  }

  const renderPost = (post) => {
    const ownerName = cleanText(post.createdByNameSnapshot || profileById.get(post.createdBy)?.name || 'Member', 120);
    const responses = responsesByPost[post.id] || [];
    const isOwner = post.createdBy === uid;
    const canManagePost = isAdmin || isOwner;
    const canModerateResponses = isAdmin || isOwner;
    const ownResponse = responses.find((response) => response.memberId === uid && String(response.status || 'interested') !== 'withdrawn') || null;
    const canRespond = canActAsMember && !isOwner && !ownResponse && String(post.status || 'open') === 'open';
    const showMemberProfileHint = uid && !canActAsMember && !isOwner && String(post.status || 'open') === 'open';
    const status = String(post.status || 'open');
    return (
      <article key={post.id} className="crew-finder-post-card">
        <div className="crew-finder-post-card__header">
          <span className="crew-finder-post-card__title">
            <strong>{post.title || crewFinderPostTypeLabel(post.type)}</strong>
            <small className="text-muted">
              {[crewFinderPostTypeLabel(post.type), formatPostDate(post), post.eventNameSnapshot].filter(Boolean).join(' · ')}
            </small>
            <small className="text-muted">
              {ownerName}{post.boatLabelSnapshot ? ` · ${post.boatLabelSnapshot}` : ''}
            </small>
          </span>
          <span className={`status-pill ${status === 'open' ? 'success' : 'info'}`}>
            {crewFinderStatusLabel(post.status)}
          </span>
        </div>
        {post.preferredRoles && <p className="crew-finder-post-card__meta text-muted">{post.preferredRoles}</p>}
        {post.notes && <p className="crew-finder-post-card__notes">{post.notes}</p>}
        {responses.length > 0 && (
          <div className="crew-finder-response-list">
            {responses.map((response) => (
              <div key={response.id} className="crew-finder-response-row">
                <div>
                  <strong>{cleanText(response.memberNameSnapshot || profileById.get(response.memberId)?.name || 'Member', 120)}</strong>
                  {response.message && <small className="text-muted">{response.message}</small>}
                </div>
                <div className="crew-finder-response-row__meta">
                  <span className={`status-pill ${String(response.status || 'interested') === 'accepted' ? 'success' : 'info'}`}>
                    {crewFinderResponseStatusLabel(response.status)}
                  </span>
                  {canModerateResponses && String(response.status || 'interested') === 'interested' && (
                    <span className="crew-finder-response-row__actions">
                      <button type="button" className="btn btn--secondary btn--xs" onClick={() => updateResponseStatus(post, response, 'accepted')}>Accept</button>
                      <button type="button" className="btn btn--secondary btn--xs" onClick={() => updateResponseStatus(post, response, 'declined')}>Decline</button>
                    </span>
                  )}
                  {response.memberId === uid && String(response.status || 'interested') === 'interested' && (
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => updateResponseStatus(post, response, 'withdrawn')}>Withdraw</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {canRespond && (
          <div className="crew-finder-response-box">
            <p className="crew-finder-response-box__hint">{responseHelperText(post)}</p>
            <textarea
              className="input"
              placeholder="Message"
              value={responseDrafts[post.id] || ''}
              onChange={(event) => setResponseDrafts((current) => ({ ...current, [post.id]: event.target.value }))}
            />
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => respondToPost(post)}>{interestActionLabel(post)}</button>
          </div>
        )}
        {ownResponse && (
          <p className="crew-finder-post-card__hint">You have already responded: {crewFinderResponseStatusLabel(ownResponse.status)}.</p>
        )}
        {showMemberProfileHint && (
          <p className="crew-finder-post-card__hint">Admins need a member profile on this team to register interest. Use the admin tools to manage the post instead.</p>
        )}
        {canManagePost && (
          <div className="crew-finder-post-card__actions">
            {status === 'open' && (
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                onClick={() => updatePostStatus(post, post.type === 'crew_needed' ? 'filled' : 'closed')}
              >
                {primaryPostActionLabel(post)}
              </button>
            )}
            {status === 'open' && post.type === 'crew_needed' && (
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => updatePostStatus(post, 'closed')}>Close</button>
            )}
            {status === 'open' && (
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => updatePostStatus(post, 'cancelled')}>Cancel</button>
            )}
          </div>
        )}
      </article>
    );
  };

  const postTypeForForm = activeTab === 'available_to_crew' ? 'available_to_crew' : 'crew_needed';
  const canUseForm = postTypeForForm === 'available_to_crew' ? canPostAvailability : canPostCrewNeeded;
  const showsComposer = activeTab === 'crew_needed' || activeTab === 'available_to_crew' || (activeTab === 'admin' && isAdmin);
  const tabs = [
    ['crew_needed', 'Crew needed', crewNeededPosts.length],
    ['available_to_crew', 'Available crew', availabilityPosts.length],
    ['my_posts', 'My posts', myPosts.length],
    ...(isAdmin ? [['admin', 'Admin', posts.length]] : []),
  ];

  return (
    <div className="modal-form">
      <div className="crew-finder-modal">
        <header className="crew-finder-modal__header">
          <div>
            <h2>Crew Finder</h2>
          </div>
          <button type="button" className="btn btn--secondary" onClick={onClose}>Close</button>
        </header>

        <nav className="crew-finder-tabs" aria-label="Crew Finder sections">
          {tabs.map(([tab, label, count]) => (
            <button
              key={tab}
              type="button"
              className={`crew-finder-tab${activeTab === tab ? ' is-active' : ''}`}
              aria-pressed={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              <span>{label}</span>
              <strong>{count}</strong>
            </button>
          ))}
        </nav>

        {error && <p className="crew-finder-alert crew-finder-alert--error">{error}</p>}
        {success && <p className="crew-finder-alert crew-finder-alert--success">{success}</p>}

        <div className={`crew-finder-modal__body${showsComposer ? '' : ' crew-finder-modal__body--posts-only'}`}>
          {showsComposer && (
          <div className="crew-finder-modal__composer">
            {(activeTab === 'crew_needed' || activeTab === 'available_to_crew') && (
              <section className="crew-finder-panel crew-finder-panel--composer">
                <div className="crew-finder-panel__header">
                  <h3>{activeTab === 'available_to_crew' ? 'Post crew availability' : 'Post crew needed'}</h3>
                </div>
                {!canUseForm ? (
                  <p className="crew-finder-empty">Posting is limited by the club&apos;s Crew Finder settings.</p>
                ) : (
                  <form onSubmit={savePost} className="crew-finder-form">
                    <input type="hidden" value={postTypeForForm} />
                    <label className="form-label crew-finder-field crew-finder-field--full">
                      <span>Title</span>
                      <input
                        className={`input${visibleFieldError('title') ? ' is-invalid' : ''}`}
                        value={draft.title}
                        onChange={(event) => updateDraft('title', event.target.value)}
                        onBlur={() => markFieldTouched('title')}
                        aria-invalid={Boolean(visibleFieldError('title'))}
                      />
                      {visibleFieldError('title') && <small className="crew-finder-field__error">{visibleFieldError('title')}</small>}
                    </label>

                    <div className="crew-finder-form__grid crew-finder-form__grid--time">
                      <label className="form-label crew-finder-field">
                        <span>Date</span>
                        <input
                          className={`input${visibleFieldError('dateIso') ? ' is-invalid' : ''}`}
                          type="date"
                          value={draft.dateIso}
                          onChange={(event) => updateDraft('dateIso', event.target.value)}
                          onBlur={() => markFieldTouched('dateIso')}
                          aria-invalid={Boolean(visibleFieldError('dateIso'))}
                        />
                        {visibleFieldError('dateIso') && <small className="crew-finder-field__error">{visibleFieldError('dateIso')}</small>}
                      </label>
                      <label className="form-label crew-finder-field">
                        <span>From</span>
                        <input
                          className={`input${visibleFieldError('fromTime') ? ' is-invalid' : ''}`}
                          type="time"
                          value={draft.fromTime}
                          onChange={(event) => updateDraft('fromTime', event.target.value)}
                          onBlur={() => markFieldTouched('fromTime')}
                          aria-invalid={Boolean(visibleFieldError('fromTime'))}
                        />
                        {visibleFieldError('fromTime') && <small className="crew-finder-field__error">{visibleFieldError('fromTime')}</small>}
                      </label>
                      <label className="form-label crew-finder-field">
                        <span>To</span>
                        <input
                          className={`input${visibleFieldError('toTime') ? ' is-invalid' : ''}`}
                          type="time"
                          value={draft.toTime}
                          onChange={(event) => updateDraft('toTime', event.target.value)}
                          onBlur={() => markFieldTouched('toTime')}
                          aria-invalid={Boolean(visibleFieldError('toTime'))}
                        />
                        {visibleFieldError('toTime') && <small className="crew-finder-field__error">{visibleFieldError('toTime')}</small>}
                      </label>
                    </div>

                    <div className="crew-finder-form__grid">
                      <label className="form-label crew-finder-field">
                        <span>Event</span>
                        <select className="input" value={draft.eventId} onChange={(event) => updateDraft('eventId', event.target.value)}>
                          <option value="">No linked event</option>
                          {events.map((event) => <option key={event.id} value={event.id}>{event.name || event.title || 'Event'}</option>)}
                        </select>
                      </label>
                      {postTypeForForm === 'crew_needed' && (
                        <label className="form-label crew-finder-field">
                          <span>Boat source</span>
                          <select className="input" value={effectiveBoatSourceType} onChange={(event) => updateBoatSourceType(event.target.value)}>
                            {boatSourceOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                      )}
                    </div>

                    {postTypeForForm === 'crew_needed' && (
                      effectiveBoatSourceType === 'club_boat' ? (
                        <label className="form-label crew-finder-field crew-finder-field--full">
                          <span>Club boat</span>
                          <select className="input" value={draft.clubBoatId} onChange={(event) => updateDraft('clubBoatId', event.target.value)}>
                            <option value="">Choose a club boat</option>
                            {boats.map((boat) => <option key={boat.id} value={boat.id}>{safeCrewFinderBoatLabel({ boatSourceType: 'club_boat', clubBoat: boat })}</option>)}
                          </select>
                        </label>
                      ) : effectiveBoatSourceType === 'club_boat_booking' ? (
                        <label className="form-label crew-finder-field crew-finder-field--full">
                          <span>Boat booking</span>
                          <select className="input" value={draft.boatBookingRequestId} onChange={(event) => updateDraft('boatBookingRequestId', event.target.value)}>
                            <option value="">Choose a boat booking request</option>
                            {boatRequests.map((request) => <option key={request.id} value={request.id}>{formatBookingRequestLabel(request, boatsById)}</option>)}
                          </select>
                        </label>
                      ) : effectiveBoatSourceType === 'mooring_allocation' ? (
                        <label className="form-label crew-finder-field crew-finder-field--full">
                          <span>Mooring vessel</span>
                          <select className="input" value={draft.mooringAllocationId} onChange={(event) => updateDraft('mooringAllocationId', event.target.value)}>
                            <option value="">Choose a mooring vessel</option>
                            {moorings.map((allocation) => <option key={allocation.id} value={allocation.id}>{safeCrewFinderBoatLabel({ boatSourceType: 'mooring_allocation', mooringAllocation: allocation })}</option>)}
                          </select>
                        </label>
                      ) : (
                        <label className="form-label crew-finder-field crew-finder-field--full">
                          <span>Boat or arrangement</span>
                          <input
                            className="input"
                            value={draft.boatLabel}
                            onChange={(event) => updateDraft('boatLabel', event.target.value)}
                            placeholder="e.g. My Laser, J/70, boat TBC"
                          />
                        </label>
                      )
                    )}

                    <label className="form-label crew-finder-field crew-finder-field--full">
                      <span>Roles or availability</span>
                      <input className="input" value={draft.preferredRoles} onChange={(event) => updateDraft('preferredRoles', event.target.value)} placeholder={postTypeForForm === 'crew_needed' ? 'e.g. helm, foredeck, trimmer' : 'e.g. evenings, racing, cruising'} />
                    </label>
                    <label className="form-label crew-finder-field crew-finder-field--full">
                      <span>Notes</span>
                      <textarea className="input" value={draft.notes} onChange={(event) => updateDraft('notes', event.target.value)} />
                    </label>
                    <div className="crew-finder-form__actions"><button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving...' : 'Save post'}</button></div>
                  </form>
                )}
              </section>
            )}

            {activeTab === 'admin' && isAdmin && (
              <section className="crew-finder-panel crew-finder-panel--composer">
                <div className="crew-finder-panel__header">
                  <h3>Settings</h3>
                </div>
                <form onSubmit={saveSettings} className="crew-finder-form">
                  <label className="form-label crew-finder-field crew-finder-field--full">
                    <span>Posting access</span>
                    <select className="input" value={settingsDraft.postingMode} onChange={(event) => setSettingsDraft((current) => ({ ...current, postingMode: event.target.value }))}>
                      <option value="admins_only">Admins only</option>
                      <option value="all_members">All members</option>
                      <option value="tagged_members">Members with selected tags</option>
                    </select>
                  </label>
                  {settingsDraft.postingMode === 'tagged_members' && (
                    <div className="crew-finder-tag-grid">
                      {editableTagNames.map((tagName) => (
                        <label key={tagName} className="crew-finder-tag-option">
                          <input
                            type="checkbox"
                            checked={settingsDraft.postingTagNames.includes(tagName)}
                            onChange={() => setSettingsDraft((current) => {
                              const set = new Set(current.postingTagNames || []);
                              if (set.has(tagName)) set.delete(tagName);
                              else set.add(tagName);
                              return { ...current, postingTagNames: Array.from(set) };
                            })}
                          />
                          {tagName}
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="crew-finder-toggle-list">
                    <label className="form-toggle-row"><span>Allow crew-needed posts</span><input type="checkbox" checked={settingsDraft.allowCrewNeededPosts !== false} onChange={(event) => setSettingsDraft((current) => ({ ...current, allowCrewNeededPosts: event.target.checked }))} /></label>
                    <label className="form-toggle-row"><span>Allow availability posts</span><input type="checkbox" checked={settingsDraft.allowAvailabilityPosts !== false} onChange={(event) => setSettingsDraft((current) => ({ ...current, allowAvailabilityPosts: event.target.checked }))} /></label>
                  </div>
                  <div className="crew-finder-form__actions"><button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</button></div>
                </form>
              </section>
            )}
          </div>
          )}

          <section className="crew-finder-panel crew-finder-modal__posts">
            <div className="crew-finder-panel__header">
              <h3>{activeTab === 'available_to_crew' ? 'Available crew' : activeTab === 'my_posts' ? 'My posts' : activeTab === 'admin' ? 'All posts' : 'Crew needed'}</h3>
            </div>
            <div className="crew-finder-post-list">
              {visiblePosts.length === 0 ? <p className="crew-finder-empty">No Crew Finder posts here yet.</p> : visiblePosts.map(renderPost)}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
